import React, { useEffect, useState } from "react";

// BridgeLoader — the full-screen white transition cover shown while a React route resolves (it is
// also the router's "*" catch-all element).
//
// BELT-AND-BRACES (repair round 2026-07-28): this overlay is position:fixed, full-viewport,
// z-index 9999 — if whatever it is covering never finishes (the nested-activity hang was exactly
// that: a URL with no client route rendered this forever), the user is permanently trapped behind
// a pointer-blocking spinner with no errors and no exit. After STALL_MS the spinner is replaced by
// an honest stalled state with two real exits (reload / home) so a stuck load can never strand
// anyone again. The timer is cleared on unmount, so a normal fast transition never sees it.
export const STALL_MS = 12000;

const containerStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "fixed",
  left: 0,
  top: 0,
  width: "100dvw",
  height: "100dvh",
  background: "#fff",
  zIndex: 9999,
};

const ellipsisStyle = {
  display: "inline-block",
  position: "relative",
  width: 80,
  height: 80,
};

const dotBase = {
  position: "absolute",
  top: 80 / 2.4,
  width: 80 / 6,
  height: 80 / 6,
  borderRadius: "50%",
  background: "#633AFB",
  animationTimingFunction: "cubic-bezier(0, 1, 1, 0)",
};

const stalledCardStyle = {
  maxWidth: 420,
  padding: "0 24px",
  textAlign: "center",
  fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, sans-serif",
  color: "#0F172A",
};

const stalledButtonStyle = {
  appearance: "none",
  border: "none",
  cursor: "pointer",
  background: "#633AFB",
  color: "#fff",
  fontFamily: "inherit",
  fontSize: 14,
  fontWeight: 700,
  borderRadius: 10,
  padding: "11px 20px",
};

const stalledLinkStyle = {
  display: "inline-block",
  marginLeft: 14,
  fontSize: 14,
  fontWeight: 700,
  color: "#633AFB",
  textDecoration: "none",
};

const BridgeLoader = () => {
  const [stalled, setStalled] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStalled(true), STALL_MS);
    return () => clearTimeout(t);
  }, []);

  if (stalled) {
    return (
      <div style={containerStyle} role="alert">
        <div style={stalledCardStyle}>
          <h2 style={{ fontSize: 18, fontWeight: 800, margin: "0 0 8px" }}>
            This page is taking too long to load
          </h2>
          <p style={{ fontSize: 13.5, lineHeight: 1.5, color: "#64748B", margin: "0 0 18px" }}>
            Something got stuck. Reloading usually fixes it — or head back to your dashboard.
          </p>
          <button type="button" style={stalledButtonStyle} onClick={() => window.location.reload()}>
            Reload the page
          </button>
          <a href="/" style={stalledLinkStyle}>Go to dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} role="status" aria-live="polite" aria-label="Loading">
      <style>{`
        @keyframes lds-ellipsis1 { 0% { transform: scale(0); } 100% { transform: scale(1); } }
        @keyframes lds-ellipsis2 { 0% { transform: translate(0, 0); } 100% { transform: translate(24px, 0); } }
        @keyframes lds-ellipsis3 { 0% { transform: scale(1); } 100% { transform: scale(0); } }
      `}</style>

      <div style={ellipsisStyle} className="lds-ellipsis" aria-hidden>
        <div style={{ ...dotBase, left: 8, animation: "lds-ellipsis1 0.6s infinite" }} />
        <div style={{ ...dotBase, left: 8, animation: "lds-ellipsis2 0.6s infinite" }} />
        <div style={{ ...dotBase, left: 32, animation: "lds-ellipsis2 0.6s infinite" }} />
        <div style={{ ...dotBase, left: 56, animation: "lds-ellipsis3 0.6s infinite" }} />
      </div>
    </div>
  );
};

export default BridgeLoader;
